import React, { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import PulseLoader from "react-spinners/PulseLoader"
import EtContentEditorLightWithPlace from "@/utils/EtContentEditorLightWithPlace"
import { useGetHabitsQuery, useUpdateHabitMutation } from "./habitsApiSlice"
import { PlusIcon, MinusIcon} from '@heroicons/react/24/outline'

const EtUpdateHabit = ({id, setActiveAdminView}) => {
  // First Created - 26 Jun 2025
  // Author - Atul Pandey
  // Inherited from impactTypes and repurposed

  // 1 - Set Title
  useTitle("Update Habit")

  // 2 - extract the habit Id from the url
  // const { id } = useParams()
  // console.log("id:", id)

  // 3 - Extract the habit from the query to load the habit data
  const { habit } = useGetHabitsQuery("Habit", {
    selectFromResult: ({ data }) => ({
      habit: data?.entities[id],
    }),
  })

  // console.log('habit:', habit)
  // console.log("🧩 Habit timebased Instructions:", habit?.timeBasedInstructions?.[0]?.earlyMorning);


  // 4 - Check for returned data
  if (!habit) {
    return <PulseLoader color={"#ea580c"} />
  }

  // 5. Display data
  let content
  content = <EtUpdateHabitForm habit={habit} id={id} setActiveAdminView={setActiveAdminView}/> // 27 Feb 2025 - added setActiveAdminView={setActiveAdminView}
  return content
}

const EtUpdateHabitForm = ({ habit, id, setActiveAdminView}) =>{
  
  // 1 - set the variable for navigate
  const navigate = useNavigate()
  

  //console.log('update form invoked!')

  // 2 - set the mutation to create the new section data
  const[updateHabit, {
    isLoading,
    isSuccess,
    isError,
    error,
  }] = useUpdateHabitMutation()

// 3 - set the form data object
// const [formData, setFormData] = useState({
//   habitName: habit.habitName,
//   habitDesc: habit.habitDesc,
//   habitPlexus: habit.habitPlexus || "Physical",
//   habitTimeSegments: habit.habitTimeSegments || ["Morning"],
//   // habitReadinessArea: habit.habitReadinessArea || "Personal Readiness",
//   habitReadinessArea: habit.habitReadinessArea || ["Personal Readiness"],
//   habitFocusArea: habit.habitFocusArea || [],
//   habitInstructions: habit.habitInstructions || [],
//   timeBasedInstructions: habit.timeBasedInstructions || [{
//     earlyMorning: [],
//     morning: [],
//     midDay: [],
//     evening: [],
//     night: [],
//   }],
//   aboutMe: habit.aboutMe,
//   pic: habit.pic || "",
// })

const [formData, setFormData] = useState(() => JSON.parse(JSON.stringify({
  habitName: habit?.habitName,
  habitDesc: habit?.habitDesc,
  habitPlexus: habit?.habitPlexus || "Physical",
  habitTimeSegments: habit?.habitTimeSegments || ["Morning"],
  habitReadinessArea: habit?.habitReadinessArea || ["Personal Readiness"],
  habitFocusArea: habit?.habitFocusArea || [],
  habitInstructions: habit?.habitInstructions || [],
  timeBasedInstructions: habit?.timeBasedInstructions || [{
    earlyMorning: [], morning: [], midDay: [], evening: [], night: [],
  }],
  aboutMe: habit?.aboutMe,
  pic: habit?.pic || "",
})))


const [deletedInstructions, setDeletedInstructions] = useState({
    earlyMorning: [],
    morning: [],
    midDay: [],
    evening: [],
    night: [],
  });



//console.log('formData for update:', formData)

// 4 - Reset form upon successful submission
useEffect(() => {
  if(isSuccess) {
    setFormData({
      habitName: "",
      habitDesc: "",
      habitPlexus: "",
      habitTimeSegments: [],
      habitReadinessArea: [],
      habitFocusArea: [],
      habitInstructions: [],
      timeBasedInstructions: [                              // 27 Jun 2025
        {
          earlyMorning: [],
          morning: [],
          midDay: [],
          evening: [],
          night: [],
        },
      ],
      aboutMe: "",
      pic: "",
    })

    setDeletedInstructions({ // 27 Jun 2025
        earlyMorning: [],
        morning: [],
        midDay: [],
        evening: [],
        night: [],
      })

    setActiveAdminView('habits'); 
  }

},[isSuccess]) 

// 5 - Form field updates / change handling
const handleChange = (e) => {
  const { name, value, files } = e.target
  if (name === "pic") {
    setFormData(prevState => ({ ...prevState, pic: files[0] }))
  } else {
    setFormData(prevState => ({ ...prevState, [name]: value }))
  }
}

// 5b - Generic handlers
const handleAdd = (fieldName, newItem) => {
  setFormData(prev => ({
    ...prev,
    [fieldName]: [...prev[fieldName], newItem]
  }))
}

const handleRemove = (fieldName, index) => {
  setFormData(prev => ({
    ...prev,
    [fieldName]: prev[fieldName].filter((_, i) => i !== index)
  }))
}

const handleFieldChange = (fieldName, index, value) => {
  const updated = formData[fieldName].map((item, i) => i === index ? value : item)
  setFormData(prev => ({ ...prev, [fieldName]: updated }))
}

// 5c - Handle Instructions
const handleAddInstruction = () => handleAdd("habitInstructions", "")
const handleRemoveInstruction = (index) => handleRemove("habitInstructions", index)
const handleInstructionChange = (index, value) => handleFieldChange("habitInstructions", index, value)

// 5d - Handle Time Based Instructions - 27 Jun 2025
const handleAddTimeInstruction = (segment) => {
  setFormData((prev) => {
    const copy = JSON.parse(JSON.stringify(prev)); // deep clone safely

    // 29 Jun 2025 - Added to prevent mutation errors when the array doesn’t exist or is malformed
    if (!Array.isArray(copy.timeBasedInstructions) || !copy.timeBasedInstructions[0]) {
      copy.timeBasedInstructions = [{
        earlyMorning: [],
        morning: [],
        midDay: [],
        evening: [],
        night: [],
      }];
    }


    copy.timeBasedInstructions[0][segment] = [
      ...(copy.timeBasedInstructions[0][segment] || []),
      { instruction: "", createdAt: new Date(), updatedAt: new Date() },
    ];

    return copy;
  });
};

const handleRemoveTimeInstruction = (segment, index) => {
  setFormData(prev => {
    const copy = { ...prev };
    const item = copy.timeBasedInstructions[0][segment][index];

    // ✅ 2. Handle delete vs splice
    if (item?._id) {
      copy.timeBasedInstructions[0][segment][index] = {
        ...item,
        delete: true,
      };
    } else {
      copy.timeBasedInstructions[0][segment].splice(index, 1);
    }

    return { ...copy };
  });
};

const handleTimeInstructionChange = (segment, index, value) => {
  setFormData((prev) => {
    const copy = { ...prev };
    copy.timeBasedInstructions[0][segment][index].instruction = value;
    copy.timeBasedInstructions[0][segment][index].updatedAt = new Date();
    return copy;
  });
};

// 6 - function to strip html tags from sections
function stripHtml(html) {
  return html.replace(/<\/?[^>]+(>|$)/g, "");
}

// 7 - Form Submit Logic
const handleSubmit = async (e) => {
e.preventDefault()

// Initialize an object to collect the form data
const habitData = {
  habitName: formData.habitName,
  habitDesc: formData.habitDesc,
  habitPlexus: formData.habitPlexus,
  habitTimeSegments: formData.habitTimeSegments,
  habitReadinessArea: formData.habitReadinessArea,
  habitFocusArea: formData.habitFocusArea,
  habitInstructions: formData.habitInstructions,
  //27 Jun 2025
  timeBasedInstructions: formData.timeBasedInstructions.map(group => {
    const filteredGroup = {};
    for (const segmentKey of ['earlyMorning', 'morning', 'midDay', 'evening', 'night']) {
      const segmentArray = group[segmentKey] || [];

      filteredGroup[segmentKey] = segmentArray
        .filter(item => item.instruction?.trim() || item._id || item.delete)
        .map(item => {
          const cleaned = {
            instruction: item.instruction?.trim() || '',
            updatedAt: item.updatedAt,
          };

          if (item._id) cleaned._id = item._id;
          if (item.createdAt) cleaned.createdAt = item.createdAt;
          if (item.delete) cleaned.delete = true;

          return cleaned;
        });
    }
    return filteredGroup;
  }),
  aboutMe: formData.aboutMe,
}


//console.log('habit Name:',habitData.habitName)

if (formData.pic instanceof Blob) {
  const reader = new FileReader();
  reader.onloadend = async () => {
    habitData.pic = reader.result;
    try {
      habitData.id = id;
      console.log("📤 Form submitted with new image.");
      await updateHabit(habitData).unwrap();
    } catch (error) {
      console.error(error);
    }
  };
  reader.readAsDataURL(formData.pic);
} else {
  try {
    habitData.id = id;
    // console.log("📤 Form submitted without new image.");
    await updateHabit(habitData).unwrap();
  } catch (error) {
    console.error(error);
  }
}

}

// 8 - Handle Cancel

const handleCancel = () => {
  // navigate("/habits")
  setActiveAdminView('habits'); // 27 Feb 2025
}

// 9 - Handle Clear
const handleClear = () => {
  setFormData({
      habitName: "",
      habitDesc: "",
      habitPlexus: "",
      habitTimeSegments: [],
      habitReadinessArea: [],
      habitFocusArea: [],
      habitInstructions: [],
      aboutMe: "",
      pic: "",
    })
}

return (
  <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black">
    {isLoading && <PulseLoader color={"#ea580c"} />}
    {isError && (
      <p className="text-orange-700 font-semi-bold py-2">
        {error?.data?.message}
      </p>
    )}
    <div className="bg-white p-1 m-1 rounded">
      <p className="p-1 m-1 font-bold font-poppins text-xl">Update a Habit</p>

      <div className="bg-gray-200 rounded m-1 p-1">
        <form
          onSubmit={handleSubmit}
          className="m-1 p-1 text-xs font-poppins"
        >
          
          <div id="habitName">
            <input
              type="text"
              name="habitName"
              placeholder="Enter the habit name here"
              value={formData.habitName}
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
            />
          </div>

          <div className="bg-white w-full rounded p-2 m-1">
            <EtContentEditorLightWithPlace
              value={formData.habitDesc}
              handleChange={(content) =>
                setFormData({ ...formData, habitDesc: content })
              }
              placeholderText={
                "Write this habit is about or will be used for"
              }
              // toolbarId={'sectionDesc'}
            />
          </div>

          {/* Habit Instuctions */}
          <div id="habitInstructions" className="m-1 py-1 border border-white rounded">
            <p className="font-bold mx-2 px-2 mt-2">Habit Instructions</p>
            <p className="text-[8px] mx-2 px-2 mb-2 italic text-gray-600">Steps or guidance to perform this habit</p>

            {formData.habitInstructions.map((instruction, index) => (
              <div key={index} className="flex justify-start items-center mx-1">
                <input
                  type="text"
                  value={instruction}
                  onChange={(e) => handleInstructionChange(index, e.target.value)}
                  placeholder="Enter an instruction"
                  className="w-full rounded p-2 m-1 text-xs"
                />
                <div className="rounded bg-red-500 m-1 p-1">
                  <button type="button" onClick={() => handleRemoveInstruction(index)}>
                    <MinusIcon className="h-5 w-5 text-white" />
                  </button>
                </div>
              </div>
            ))}

            <div className="flex justify-start items-center m-1">
              <p className="font-bold text-xs m-1 p-1">Add</p>
              <button type="button" onClick={handleAddInstruction}>
                <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-green-500" />
              </button>
            </div>
          </div>

          
         {/* Time-Based Instructions – 27 Jun 2025 */}
        <div id="timeBasedInstructions" className="m-1 py-1 border border-white rounded">
          <p className="font-bold mx-2 px-2 mt-2">Time-Based Instructions</p>
          <p className="text-[8px] mx-2 px-2 mb-2 italic text-gray-600">Define instructions for specific times of the day</p>

          {['earlyMorning', 'morning', 'midDay', 'evening', 'night'].map((segmentKey) => (
            <div key={segmentKey} className="bg-gray-100 m-2 p-2 rounded">
              <p className="font-bold text-sm capitalize">{segmentKey.replace(/([A-Z])/g, ' $1')}</p>
              {(formData.timeBasedInstructions?.[0]?.[segmentKey] || [])
                .filter(entry => !entry.delete) // Exclude marked-for-deletion entries from display
                .map((entry, eIndex) => (
                <div key={eIndex} className="flex items-center space-x-2 mt-1">
                  <input
                    type="text"
                    value={entry.instruction}
                    onChange={(e) =>
                      handleTimeInstructionChange(segmentKey, eIndex, e.target.value)
                    }
                    className="w-full p-1 rounded text-xs"
                    placeholder={`Instruction for ${segmentKey}`}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveTimeInstruction(segmentKey, eIndex)}
                    className="bg-red-500 text-white p-1 rounded"
                  >
                    <MinusIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}

              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => handleAddTimeInstruction(segmentKey)}
                  className="bg-green-500 text-white px-2 py-1 rounded flex items-center space-x-1"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span className="text-xs">Add</span>
                </button>
              </div>
            </div>
          ))}
        </div>




          {/* Plexus Dropdown */}
          <div className="m-1 p-1">
            <label className="block font-semibold">Plexus</label>
            <select
              name="habitPlexus"
              value={formData.habitPlexus}
              onChange={handleChange}
              className="w-full rounded p-2 mt-1"
            >
              <option>Physical</option>
              <option>Mental</option>
              <option>Emotional</option>
              <option>Intellectual</option>
              <option>Spiritual</option>
            </select>
          </div>

           {/* Time Segments Checkboxes */}    
          <div className="m-1 p-1">
            <label className="block font-semibold">Time Segments</label>
            {["Early Morning", "Morning", "Mid Day", "Evening", "Night"].map(segment => (
              <label key={segment} className="inline-flex items-center mr-4">
                <input
                  type="checkbox"
                  value={segment}
                  checked={formData.habitTimeSegments.includes(segment)}
                  onChange={(e) => {
                    const newSegments = formData.habitTimeSegments.includes(segment)
                      ? formData.habitTimeSegments.filter(s => s !== segment)
                      : [...formData.habitTimeSegments, segment];
                    setFormData({ ...formData, habitTimeSegments: newSegments });
                  }}
                  className="mr-1"
                />
                {segment}
              </label>
            ))}
          </div>

           {/* Readiness Area Multi-Select Checkboxes  - 26 Jun 2025*/}
          {/* <div className="m-1 p-1">
            <label className="block font-semibold">Readiness Area</label>
            <select
              name="habitReadinessArea"
              value={formData.habitReadinessArea}
              onChange={handleChange}
              className="w-full rounded p-2 mt-1"
            >
              <option>Personal Readiness</option>
              <option>Contextual Readiness</option>
              <option>Capabilities Readiness</option>
              <option>Leadership Readiness</option>
            </select>
          </div> */}
          
          <div className="p-2 m-1 bg-white rounded">
            <p className="m-1 p-1 font-bold border-b">Select one or more Readiness Areas this habit will impact</p>
            {[
              "Personal Readiness",
                "Contextual Readiness",
                "Capabilities Readiness",
                "Leadership Readiness"
            ].map((area) => (
              <label key={area} className="block ml-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.habitReadinessArea.includes(area)}
                  onChange={() => {
                    setFormData(prev => {
                      const exists = prev.habitReadinessArea.includes(area);
                      const updated = exists
                        ? prev.habitReadinessArea.filter(a => a !== area)
                        : [...prev.habitReadinessArea, area];
                      return { ...prev, habitReadinessArea: updated };
                    });
                  }}
                />
                <span className="ml-2">{area}</span>
              </label>
            ))}
          </div>

          {/* Focus Area Multi-Select Checkboxes */}
          <div className="p-2 m-1 bg-white rounded">
           <p className="m-1 p-1 font-bold border-b">Select one or more Change Areas this habit will impact</p>
            {[
              "PR: Strengthen Exec Presence",
              "PR: Strengthen Energy",
              "PR: Strengthen Self Awareness",
              "PR: Strengthen CORE VALUEs",
              "PR: Strengthen PURPOSE",
              "PR: Strengthen Thought Harnessing",
              "PR: Strengthen Capabilties",
              "PR: Strengthen Dispassion",
              "PR: Strengthen WILL",
              "PR: Strengthen HABITs",
              "CR: Deepen Relationships",
              "CR: Manage Reactions",
              "CR: Manage Expectations",
              "CR: Align PURPOSE",
              "CR: Strengthen Influence",
              "CR: Handling Praise",
              "CR: Handling Criticism",
              "CR: Handling Success",
              "CR: Handling Failures",
              "CR: Handling Distractions",
            ].map(area => (
              <label key={area} className="block ml-2 text-sm">
                <input
                  type="checkbox"
                  value={area}
                  checked={formData.habitFocusArea.includes(area)}
                  onChange={(e) => {
                    const updated = formData.habitFocusArea.includes(area)
                      ? formData.habitFocusArea.filter(f => f !== area)
                      : [...formData.habitFocusArea, area];
                    setFormData({ ...formData, habitFocusArea: updated });
                  }}
                  className="mr-1"
                />
                {area}
              </label>
            ))}
          </div>

          <div id="aboutMe">
            <input
              type="text"
              name="aboutMe"
              placeholder="Add something about the personality about this habit"
              value={formData.aboutMe}
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
            />
          </div>

          <div id="pic">
            <input
              type="file"
              name="pic"
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
            />
          </div>

         <div id="impacts" className="bg-white m-1 px-1 py-3 rounded">
            <EtBtnFormBasic
              name={"Update"}
              type={"create"}
              handleClick={handleSubmit}
            />
            <EtBtnFormBasic
              name={"Clear"}
              type={"clear"}
              handleClick={handleClear}
            />
            <EtBtnFormBasic
              name={"Cancel"}
              type={"cancel"}
              handleClick={handleCancel}
            />
          </div>
        </form>
      </div>
    </div>
  </div>
)

}

export default EtUpdateHabit